import { Stack } from '@mui/material';
import Decimal from 'decimal.js';
import { compact } from 'lodash';
import { useMemo, useRef } from 'react';

import { InfoCircleIcon } from '@/components/icons/InfoCircleIcon';
import { Badge, BadgeVariants } from '@/components/notifications/Badge/Badge';
import { PopperInner } from '@/components/poppers/Popper/Popper';
import { PopperContent } from '@/components/poppers/PopperContent';
import { EmptyCell } from '@/components/tables/DataTable/renderers/cell/EmptyCell';
import { TextRenderer } from '@/components/tables/DataTable/renderers/cell/TextRenderer';
import { Column } from '@/components/tables/DataTable/types';
import { EMPTY_CONTENT_HYPHEN } from '@/components/typography/placeholders';
import { useDelayedCloseBehavior } from '@/hooks/useDelayedCloseBehavior';
import { IntegrationLinkedIcon } from '@/modules/assetProviderIntegrations/shared/IntegrationLinkedIcon';
import { useCurrentUser } from '@/modules/authentication/hooks/useCurrentUser';
import { useFeatureFlag } from '@/modules/featureFlags/useFeatureFlag';
import { PrivateHouseholdIndicator } from '@/modules/household/components/PrivateHouseholdIndicator';
import { ValidationQuestionsBadge } from '@/modules/validationQuestions/ValidationQuestionsBadge';
import { COLORS } from '@/styles/tokens/colors';
import { FONT_WEIGHTS } from '@/styles/tokens/fonts';
import { formatCurrencyNoDecimalsAccounting } from '@/utils/formatting/currency';
import { formatDateToMMDDYY } from '@/utils/formatting/dates';
import { englishJoinStringList } from '@/utils/formatting/strings';

import { ClientTable } from '../ClientListPage.utils';

function NotesPopper({ notes }: { notes: string }) {
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const { isOpen, setIsMouseInTargetArea } = useDelayedCloseBehavior();

  return (
    <>
      <div
        ref={anchorRef}
        onMouseEnter={() => setIsMouseInTargetArea(true)}
        onMouseLeave={() => setIsMouseInTargetArea(false)}
        style={{ display: 'flex', alignItems: 'center' }}
      >
        <InfoCircleIcon size={16} sx={{ color: COLORS.TEXT.SECONDARY }} />
      </div>
      <PopperInner
        anchorEl={anchorRef.current}
        open={isOpen}
        placement="right"
        onMouseEnter={() => setIsMouseInTargetArea(true)}
        onMouseLeave={() => setIsMouseInTargetArea(false)}
        modifiers={[
          {
            name: 'offset',
            options: { offset: [0, 8] },
          },
        ]}
      >
        <PopperContent body={notes} />
      </PopperInner>
    </>
  );
}

export interface UseColumnsParams {
  showSubBrand: boolean;
  defaultTenantDisplayName?: string | null;
  hasTeams: boolean;
}

export function useColumns({
  showSubBrand,
  defaultTenantDisplayName,
  hasTeams,
}: UseColumnsParams) {
  const currentUser = useCurrentUser();
  const enhanced = useFeatureFlag('enhanced_household_onboarding_flow');
  const isCollaborator = currentUser?.collaborator !== null;

  return useMemo(() => {
    const columns: Column<ClientTable>[] = compact([
      {
        field: 'displayName',
        headerName: 'Client',
        minWidth: 200,
        flex: 1,
        sortable: true,
        renderCell: TextRenderer({
          textAccompaniment: ({ row }) => {
            return (
              <Stack direction="row" alignItems="center" spacing={0.5}>
                {enhanced && row.hasPendingValidationQuestions && (
                  <ValidationQuestionsBadge householdId={row.id} />
                )}
                {row.integrationProvider && (
                  <IntegrationLinkedIcon
                    providerName={row.integrationProvider}
                  />
                )}
                {row.private && <PrivateHouseholdIndicator />}
                {row.notes && <NotesPopper notes={row.notes} />}
              </Stack>
            );
          },
          rightContent: ({ row }) => {
            if (!row.isProspect && !row.isArchived) return null;

            return (
              <Stack direction="row" spacing={0.5}>
                {row.isProspect && (
                  <Badge variant={BadgeVariants.Primary} display="Prospect" />
                )}
                {row.isArchived && (
                  <Badge
                    variant={BadgeVariants.SecondaryFilled}
                    display="Archived"
                  />
                )}
              </Stack>
            );
          },
          textProps: { fontWeight: FONT_WEIGHTS.bold },
        }),
      },
      {
        field: 'totalAssets',
        headerName: 'Net value',
        width: 200,
        sortable: false,
        renderCell: TextRenderer({
          textProps: { fontWeight: FONT_WEIGHTS.bold },
          text: ({ row }) =>
            formatCurrencyNoDecimalsAccounting(
              row.totalAssets ?? new Decimal(0)
            ),
        }),
      },
      {
        field: 'state',
        headerName: 'State',
        width: 120,
        sortable: false,
        renderCell: TextRenderer({
          text: ({ row }) => row.state ?? EMPTY_CONTENT_HYPHEN,
        }),
      },
      showSubBrand && {
        field: 'sub_brand_display_name',
        headerName: 'Sub-brand',
        minWidth: 200,
        sortable: false,
        renderCell: TextRenderer({
          text: ({ row }) =>
            row.sub_brand_display_name ?? defaultTenantDisplayName ?? '',
        }),
      },
      // Hide the Teams column for tenants with no teams at all.
      !isCollaborator &&
        hasTeams && {
          field: 'teams',
          headerName: 'Teams',
          minWidth: 200,
          sortable: false,
          renderCell: TextRenderer({
            text: ({ row }) =>
              row.teams.length > 0 ? (
                englishJoinStringList(row.teams)
              ) : (
                <EmptyCell />
              ),
          }),
        },
      {
        field: 'primary_relationship_owner_name',
        headerName: 'Relationship owner',
        minWidth: 175,
        flex: 1,
        sortable: false,
        renderCell: TextRenderer({
          text: ({ row }) =>
            row.primary_relationship_owner_name ?? EMPTY_CONTENT_HYPHEN,
        }),
      },
      {
        field: 'created_at',
        headerName: 'Creation date',
        width: 150,
        sortable: true,
        renderCell: TextRenderer({
          text: ({ row }) =>
            row.created_at
              ? formatDateToMMDDYY(row.created_at)
              : EMPTY_CONTENT_HYPHEN,
        }),
      },
    ]);

    return columns;
  }, [
    showSubBrand,
    defaultTenantDisplayName,
    isCollaborator,
    hasTeams,
    enhanced,
  ]);
}
